// ============================================================================
// PHOTO BLOB STORE - Binary photo storage separated from project records
// ----------------------------------------------------------------------------
// Replaces the legacy pattern of storing base64 data URLs inline in project
// records. Photos are now stored as Blobs in a dedicated Dexie table
// (`photo_blobs`), with only lightweight metadata in the project record.
//
// This eliminates the 6MB+ single-record problem that caused IndexedDB
// performance degradation and potential crashes on large projects.
//
// Display: URL.createObjectURL(blob) for instant rendering.
// Upload: Blob passed directly to presigned PUT URL (no base64 overhead).
// ============================================================================

import db from './db';
import {
    omitCanonicalBlobFields,
    requireCanonicalProjectUuid,
} from './project-media-write-boundary';

// ─── Object URL management ──────────────────────────────────────────────────
// Track active object URLs to prevent leaks. Revoke when no longer displayed.
// The owner is stored beside the URL so a one-project delete never invalidates
// previews that another open project is still rendering.
const activeUrls = new Map<any, { url: string; projectId: any }>();
// A project UUID is retired once its durable project row is deleted. Keep the
// tombstone for the rest of the browser session so a stale IndexedDB read that
// began before cleanup cannot mint/cache a fresh URL after the delete sweep.
const deletedProjectIds = new Set<string>();

/**
 * Store a photo Blob in the dedicated photo_blobs table.
 *
 * @param {Blob} blob - The compressed image Blob
 * @param {string} projectId - Associated project ID
 * @param {Object} [metadata] - Optional metadata (width, height, etc.)
 * @returns {Promise<number>} The auto-generated blob ID
 */
export async function storePhotoBlob(blob: any, projectId: any, metadata = {}) {
    if (!(blob instanceof Blob)) {
        throw new Error('storePhotoBlob requires a Blob instance');
    }
    const canonicalProjectId = requireCanonicalProjectUuid(projectId, 'storePhotoBlob');

    const id = await (db as any).photo_blobs.add({
        ...omitCanonicalBlobFields(metadata),
        blob,
        projectId: canonicalProjectId,
        sizeBytes: blob.size,
        mimeType: blob.type || 'image/jpeg',
        storedAt: new Date().toISOString(),
    });

    return id;
}

/**
 * Retrieve a photo Blob by its ID.
 *
 * @param {number} blobId
 * @returns {Promise<Blob|null>}
 */
/**
 * Legacy API retained for compatibility. Integer-only sentinel adoption is
 * deliberately disabled: without an independent import/session token, a
 * historical Project id can collide with an unrelated pending row.
 */
export async function claimSentinelPhotoBlobs(_blobIds: any[], _projectId: any): Promise<any[]> {
    return [];
}

export async function getPhotoBlob(blobId: any, expectedProjectId: string | null = null) {
    if (!blobId) return null;
    const record = await (db as any).photo_blobs.get(blobId);
    if (!record) return null;
    // INTEGRITY GUARD: photo_blobs `id` is a GLOBAL Dexie auto-increment, so a
    // photo whose metadata carries another project's blobId (e.g. a Duplicate
    // that did not clone its blob rows) would otherwise resolve the WRONG
    // project's image. When the caller names the owning project, refuse to
    // resolve a blob it does not own. (Cross-customer leakage is separately
    // impossible here: this store is per-browser/per-user IndexedDB; the guard
    // protects against cross-PROJECT confusion within a single user.)
    if (expectedProjectId != null
        && String(record.projectId ?? '') !== String(expectedProjectId)) {
        console.warn(`[photo-blob-store] Refused cross-project blob ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
        return null;
    }
    return record.blob || null;
}

/**
 * Get a displayable Object URL for a photo.
 * Caches the URL to prevent creating duplicate object URLs.
 * Call revokePhotoUrl() when the photo is no longer displayed.
 *
 * @param {number} blobId
 * @param {string|null} expectedProjectId
 * @returns {Promise<string|null>} Object URL or null
 */
export async function getPhotoUrl(blobId: any, expectedProjectId: any = null) {
    if (!blobId) return null;
    const expectedProjectKey = expectedProjectId == null ? null : String(expectedProjectId);
    if (expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey)) return null;

    // A cached URL must obey the same ownership guard as the Blob read. Blob
    // ids are global auto-increments; returning project A's cached URL to a
    // project B caller would bypass getPhotoBlob's cross-project protection.
    const cached = activeUrls.get(blobId);
    if (cached) {
        const cachedProjectKey = cached.projectId == null ? null : String(cached.projectId);
        if (cachedProjectKey != null && deletedProjectIds.has(cachedProjectKey)) return null;
        if (expectedProjectId != null
            && String(cached.projectId ?? '') !== String(expectedProjectId)) {
            console.warn(`[photo-blob-store] Refused cross-project URL ${blobId} (owner=${cached.projectId}, requested=${expectedProjectId})`);
            return null;
        }
        return cached.url;
    }

    // Read the record (rather than only its Blob) so the cache retains exact
    // project ownership for later project-scoped revocation.
    const record = await (db as any).photo_blobs.get(blobId);
    if (!record?.blob) return null;
    if (expectedProjectId != null
        && String(record.projectId ?? '') !== String(expectedProjectId)) {
        console.warn(`[photo-blob-store] Refused cross-project URL ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
        return null;
    }
    const ownerProjectKey = record.projectId == null ? expectedProjectKey : String(record.projectId);
    // Check again after the IndexedDB await. The row can have been read before
    // an explicit delete and resolve only after its bytes and URLs were swept.
    if ((expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey))
        || (ownerProjectKey != null && deletedProjectIds.has(ownerProjectKey))) return null;

    const url = URL.createObjectURL(record.blob);
    activeUrls.set(blobId, { url, projectId: record.projectId });
    return url;
}

/**
 * Revoke a previously created Object URL to free memory.
 *
 * @param {number} blobId
 */
export function revokePhotoUrl(blobId: any) {
    const active = activeUrls.get(blobId);
    if (active) {
        try { URL.revokeObjectURL(active.url); } catch { /* noop */ }
        activeUrls.delete(blobId);
    }
}

/** Revoke only one project's active photo previews. */
export function revokeProjectPhotoUrls(projectId: any): number {
    if (projectId == null || projectId === '') return 0;
    let revoked = 0;
    for (const [blobId, active] of [...activeUrls.entries()]) {
        if (String(active.projectId) !== String(projectId)) continue;
        try { URL.revokeObjectURL(active.url); } catch { /* noop */ }
        activeUrls.delete(blobId);
        revoked += 1;
    }
    return revoked;
}

/** Read-only diagnostic seam for resource-lifecycle regression tests. */
export function getActivePhotoUrlCount(projectId: any = null): number {
    if (projectId == null || projectId === '') return activeUrls.size;
    let count = 0;
    for (const active of activeUrls.values()) {
        if (String(active.projectId) === String(projectId)) count += 1;
    }
    return count;
}

/**
 * Revoke all active Object URLs. Call on project switch or page unload.
 */
export function revokeAllPhotoUrls() {
    for (const [, active] of activeUrls) {
        try { URL.revokeObjectURL(active.url); } catch { /* noop */ }
    }
    activeUrls.clear();
}

/**
 * Delete all photo Blobs for a project.
 *
 * @param {string} projectId
 * @returns {Promise<number>} Number of Blobs deleted
 */
export async function deleteProjectBlobs(projectId: any) {
    if (!projectId) return 0;
    const projectKey = String(projectId);
    // Tombstone and revoke before the first await. Any already-running read
    // observes the tombstone when it resumes; any cached preview disappears
    // immediately and cannot be recreated for this retired project UUID.
    deletedProjectIds.add(projectKey);
    revokeProjectPhotoUrls(projectId);
    const query = (db as any).photo_blobs.where('projectId').equals(projectId);
    const count = await query.count();
    await query.delete();
    return count;
}

/**
 * Get all photo Blob IDs for a project (without loading the binary data).
 *
 * @param {string} projectId
 * @returns {Promise<Array<{id: number, sizeBytes: number, mimeType: string}>>}
 */
export async function getProjectBlobIds(projectId: any) {
    if (!projectId) return [];
    const records: any[] = await (db as any).photo_blobs.where('projectId').equals(projectId).toArray();
    return records.map(r => ({
        id: r.id,
        sizeBytes: r.sizeBytes,
        mimeType: r.mimeType,
        storedAt: r.storedAt,
    }));
}

/**
 * Clone a project's photo blobs into a NEW project. Because photo_blobs `id` is
 * a global auto-increment, a Duplicate that simply kept the source photos'
 * blobId refs would (a) render the SOURCE project's bytes and (b) lose its
 * photos entirely once the source is deleted (deleteProjectBlobs removes by
 * projectId). This reads each source blob and stores a fresh copy owned by
 * targetProjectId, returning a new photos array with the blobId refs remapped
 * to the new rows. A missing blob is tolerated only when the same photo has a
 * portable cloud receipt which the backend duplication boundary can re-home.
 * The only local blobId must never be silently discarded: reject so App.jsx can
 * roll back every target-owned row and leave the source project untouched.
 *
 * @param {Array} photos - the source project's photo metadata array
 * @param {string} sourceProjectId - the source project's id
 * @param {string} targetProjectId - the new (duplicate) project's id
 * @returns {Promise<Array>} photos with blobId refs remapped to the clones
 */
export async function cloneProjectBlobs(photos: any, sourceProjectId: any, targetProjectId: any) {
    if (!Array.isArray(photos) || !sourceProjectId || !targetProjectId) {
        return Array.isArray(photos) ? photos : [];
    }
    const clonedIds = new Map<string, any>();
    const createdTargetBlobIds: any[] = [];
    const out: any[] = [];
    try {
        for (const photo of photos) {
            if (!photo || typeof photo !== 'object' || !photo.blobId) {
                out.push(photo);
                continue;
            }
            const cloneKey = String(photo.blobId);
            let newBlobId = clonedIds.get(cloneKey);
            if (!clonedIds.has(cloneKey)) {
                const srcBlob = await getPhotoBlob(photo.blobId, sourceProjectId);
                if (srcBlob) {
                    newBlobId = await storePhotoBlob(srcBlob, targetProjectId, {
                        width: photo.width,
                        height: photo.height,
                    });
                    createdTargetBlobIds.push(newBlobId);
                    clonedIds.set(cloneKey, newBlobId);
                } else {
                    const hasPortableCloudReceipt = !!(
                        photo.s3Key || photo.fileMetadataId || photo.cloudObjectKey
                    );
                    if (!hasPortableCloudReceipt) {
                        const error: any = new Error(
                            `Cannot duplicate photo "${photo.name || photo.originalName || photo.id || photo.blobId}": `
                            + `its only browser-local blob ${photo.blobId} is unavailable.`,
                        );
                        error.code = 'DUPLICATE_LOCAL_MEDIA_UNAVAILABLE';
                        error.mediaKind = 'photo';
                        error.blobId = photo.blobId;
                        throw error;
                    }
                    newBlobId = null;
                    clonedIds.set(cloneKey, null);
                }
            }
            if (!newBlobId) {
                const { blobId: _dropped, ...rest } = photo;
                out.push(rest);
                continue;
            }
            out.push({ ...photo, blobId: newBlobId });
        }
        return out;
    } catch (error) {
        await Promise.allSettled(createdTargetBlobIds.map((blobId) => (
            (db as any).photo_blobs.delete(blobId)
        )));
        throw error;
    }
}

/**
 * Store a photo and return the updated photo metadata object (without the data URL).
 * This is the main entry point for new photo storage - call this instead of
 * setting photo.data = base64String.
 *
 * @param {Blob} blob - Compressed image Blob
 * @param {string} projectId
 * @param {Object} photoMeta - Existing photo metadata (name, width, height, etc.)
 * @returns {Promise<Object>} Updated photo metadata with blobId, without data
 */
export async function storePhoto(blob: any, projectId: any, photoMeta: any = {}) {
    const blobId = await storePhotoBlob(blob, projectId, {
        width: photoMeta.width,
        height: photoMeta.height,
    });

    // Return clean metadata - no inline data, just the reference
    return {
        ...photoMeta,
        blobId,
        sizeKB: Math.round(blob.size / 1024),
        data: undefined, // explicitly remove any inline data
    };
}

/**
 * Convert a base64 data URL to a Blob.
 * Used during migration from legacy inline storage.
 *
 * @param {string} dataUrl - data:image/jpeg;base64,/9j/4AAQ...
 * @returns {Promise<Blob>}
 */
export async function dataUrlToBlob(dataUrl: any) {
    // Manual base64 decode - more reliable than fetch() which can fail
    // on data URLs due to CSP restrictions or size limits in some browsers.
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:([^;]+)/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

const PHOTO_DATA_LANES = ['data', 'dataUrl', 'originalData', 'aiMarkupData'];

function photoHasPortableBacking(photo: any): boolean {
    return !!(photo?.s3Key || photo?.fileMetadataId || photo?.cloudObjectKey);
}

function withoutPhotoDataLanes(photo: any, predicate: (value: unknown) => boolean = () => true) {
    const next = { ...photo };
    for (const lane of PHOTO_DATA_LANES) {
        if (predicate(next[lane])) next[lane] = undefined;
    }
    return next;
}

async function objectUrlToBlob(objectUrl: string): Promise<Blob> {
    const response = await fetch(objectUrl);
    if (!response || response.ok === false) {
        throw new Error(`Object URL fetch failed${response?.status ? ` (${response.status})` : ''}`);
    }
    const blob = await response.blob();
    if (!(blob instanceof Blob)) throw new Error('Object URL did not resolve to a Blob');
    return blob;
}

/**
 * Migrate a project's inline base64 or live session-URL photos to the Blob store.
 * Idempotent - skips photos that already have a blobId.
 * Call this lazily when a project is loaded.
 *
 * @param {string} projectId
 * @param {Array} photos - The project's photos array
 * @returns {Promise<{photos: Array, migrated: number}>} Updated photos array + migration count
 */
export async function migrateProjectPhotos(projectId: any, photos: any) {
    if (!Array.isArray(photos) || photos.length === 0) {
        return { photos: photos || [], migrated: 0 };
    }

    let migrated = 0;
    const updatedPhotos = [];

    for (const photo of photos) {
        let hasUnverifiedBlobId = false;
        const inlineData = PHOTO_DATA_LANES.map(lane => photo?.[lane])
            .find(value => typeof value === 'string' && value.startsWith('data:'));
        const objectUrl = PHOTO_DATA_LANES.map(lane => photo?.[lane])
            .find(value => typeof value === 'string' && value.startsWith('blob:'));
        const hasTransientData = !!(inlineData || objectUrl);
        const hasPortableBacking = photoHasPortableBacking(photo);

        // Already migrated - the blob is the durable local source. Inline
        // display copies can now be removed before the cloud projection.
        if (photo.blobId) {
            if (hasTransientData) {
                const existingBlob = await getPhotoBlob(photo.blobId, projectId);
                if (existingBlob) {
                    updatedPhotos.push({
                        ...withoutPhotoDataLanes(photo),
                        _localBlobOnly: hasPortableBacking ? undefined : true,
                    });
                    continue;
                }
                hasUnverifiedBlobId = true;
            } else {
                updatedPhotos.push(photo);
                continue;
            }
        }

        // Peter 2026-04-20: video-frame screenshots carry `_skipBlobMigration`.
        // They're small (1-2MB total for a typical walkthrough) and never
        // uploaded to S3, so if their inline `data` gets stripped in favour
        // of an IndexedDB blobId the frames go blank on the next session
        // reload (IDB survives in some cases but not all, and any project
        // exported to another machine has no backing blob). Keep the inline
        // base64 as the single source of truth for these photos.
        if (photo._skipBlobMigration && inlineData && !objectUrl) {
            updatedPhotos.push(photo);
            continue;
        }

        // A blob: URL can still be live in the tab that created it. Resolve it
        // before stripping any lane, then persist the bytes under the exact
        // project UUID. Base64 remains the legacy fallback source.
        if (inlineData || objectUrl) {
            try {
                let blob: Blob;
                if (objectUrl) {
                    try {
                        blob = await objectUrlToBlob(objectUrl);
                    } catch (objectUrlError) {
                        if (!inlineData) throw objectUrlError;
                        blob = await dataUrlToBlob(inlineData);
                    }
                } else {
                    blob = await dataUrlToBlob(inlineData);
                }
                const blobId = await storePhotoBlob(blob, projectId, {
                    width: photo.width,
                    height: photo.height,
                });

                updatedPhotos.push({
                    ...withoutPhotoDataLanes(photo),
                    blobId,
                    sizeKB: Math.round(blob.size / 1024),
                    _localBlobOnly: hasPortableBacking ? undefined : true,
                });
                migrated++;
            } catch (err: any) {
                console.warn(`[PhotoBlobStore] Migration failed for ${photo.name || 'unknown'}:`, err?.message);
                const safeFallback = hasUnverifiedBlobId
                    ? { ...photo, blobId: undefined, _localBlobOnly: undefined }
                    : photo;
                if (objectUrl && hasPortableBacking) {
                    // The cloud receipt is portable. A dead or unstorable
                    // session URL is only a disposable preview in this case.
                    updatedPhotos.push(withoutPhotoDataLanes(
                        safeFallback,
                        value => typeof value === 'string' && value.startsWith('blob:'),
                    ));
                } else {
                    // Retain the only local locator. The persistence boundary
                    // will see the blob: URL and block the save instead of
                    // accepting metadata-only loss.
                    updatedPhotos.push(safeFallback);
                }
            }
        } else {
            // No data and no blobId - orphaned metadata, keep as-is
            updatedPhotos.push(photo);
        }
    }

    if (migrated > 0) {
        console.log(`[PhotoBlobStore] Migrated ${migrated}/${photos.length} photos from transient data to Blob store for project ${projectId}`);
    }

    return { photos: updatedPhotos, migrated };
}

/**
 * Stamp an S3 key onto a photo blob record after successful upload.
 * Called by the upload queue when a photo upload confirms.
 *
 * @param {number} blobId
 * @param {string} s3Key - The S3 object key where the photo was uploaded
 */
export async function updatePhotoS3Key(blobId: any, s3Key: any, expectedProjectId: any = null) {
    if (!blobId || !s3Key) return;
    // v5.46.72: same cross-project refusal as getPhotoBlob — ids are global
    // auto-increments, so an unguarded stamp could annotate ANOTHER project's
    // blob row with this project's s3Key.
    if (expectedProjectId != null) {
        const record = await (db as any).photo_blobs.get(blobId);
        const owner = record?.projectId;
        // A sentinel owner is unproven here: an old Project blobId can collide
        // with an unrelated pending import after a browser reset. New imports
        // have a UUID from project birth, so only exact ownership may be
        // annotated by a persisted Project reference.
        if (String(owner ?? '') !== String(expectedProjectId)) {
            console.warn(`[photo-blob-store] Refused cross-project s3Key stamp on blob ${blobId} (owner=${owner}, requested=${expectedProjectId})`);
            return;
        }
    }
    await (db as any).photo_blobs.update(blobId, { s3Key });
}

/**
 * S3 (2026-08-19): read the s3Key previously stamped on a blob row, under the
 * SAME ownership rule as updatePhotoS3Key. Exists so the save-time upload path
 * can ADOPT a key the background upload queue already earned instead of
 * re-uploading the same bytes to the same deterministic key.
 *
 * Returns null when the blob is missing, unstamped, or not already owned by
 * the exact project. Sentinel rows are deliberately unproven.
 */
export async function getPhotoBlobS3Key(blobId: any, expectedProjectId: any = null): Promise<string | null> {
    if (!blobId) return null;
    try {
        const record = await (db as any).photo_blobs.get(blobId);
        if (!record?.s3Key) return null;
        if (expectedProjectId != null) {
            const owner = record?.projectId;
            if (String(owner ?? '') !== String(expectedProjectId)) return null;
        }
        return String(record.s3Key);
    } catch {
        return null;
    }
}

/**
 * Get total storage used by all photo Blobs (in bytes).
 * Useful for the Developer page storage dashboard.
 *
 * @returns {Promise<{count: number, totalBytes: number}>}
 */
export async function getStorageStats() {
    const all: any[] = await (db as any).photo_blobs.toArray();
    return {
        count: all.length,
        totalBytes: all.reduce((sum, r) => sum + (r.sizeBytes || 0), 0),
    };
}
